import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import type { Observable} from 'rxjs';
import { map, catchError, throwError } from 'rxjs';
import { AppNotificationService } from './app-notification.service';

export interface CommonPortfolioSearchResult {
  name: string;
  portfolioId: string;
  portfolioGroupName: string;
  portfolioGroupId?: number | null;
}

export interface CreatePortfolioRequest {
  portfolioName: string;
}

export interface CreatePortfolioResponse {
  portfolioId: number;
  portfolioGroupId: number;
}

interface PortfolioSearchResponse {
  portfolio: string;
  portfolioGroupName: string;
  portfolioGroupId?: number | null;
  portfolioId: string;
}

// Schema representation of #/components/schemas/CommonPortfolio (core fields only)
// Additional properties are allowed via index signature to stay forward-compatible with backend.
export interface CommonPortfolio {
  portfolioId: string;
  portfolio: string;
  clientPortfolioName?: string; // actual name field returned by portfoliosByGroup
  portfolioGroupName: string;
  [key: string]: unknown; // fallback for any extra fields the API returns
}

@Injectable({ providedIn: 'root' })
export class CommonPortfolioService {
  private readonly http = inject(HttpClient);
  private readonly notification = inject(AppNotificationService);

  /**
   * Defensive getter that ensures https:// protocol is present
   */
  private get apiBaseUrl(): string {
    const url = environment.apiBaseUrl;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  }

  /**
   * Portfolio groups the signed-in user can reach, with no search term.
   *
   * No dedicated endpoint is needed: /api/Portfolio/portfolios already constrains
   * client_group_name to the caller's user_assigned_role entries unless they are a Super Admin,
   * and skips the name filter when the term is blank. So a blank search is exactly "my groups".
   */
  getAccessibleGroups(pageSize = 200): Observable<CommonPortfolioSearchResult[]> {
    return this.search('', pageSize);
  }

  // Public search API returning an array of results
  search(term: string, pageSize = 20): Observable<CommonPortfolioSearchResult[]> {
    const params = new HttpParams()
      .set('pageOffset', '0')
      .set('pageSize', String(pageSize))
      .set('portfolioSearchName', term);

    return this.http.get<PortfolioSearchResponse[]>(`${this.apiBaseUrl}/api/Portfolio/portfolios?`, { params }).pipe(
      map(response => {
        if (!Array.isArray(response)) return [];
        return response.map(item => ({
          name: item.portfolio,
          portfolioGroupName: item.portfolioGroupName,
          portfolioGroupId: (item as any).portfolioGroupId ?? null,
          portfolioId: item.portfolioId
        } satisfies CommonPortfolioSearchResult));
      })
    );
  }

  // Retrieve a single portfolio by id
  getPortfolioById(portfolioId: string): Observable<CommonPortfolio> {
    return this.http.get<CommonPortfolio>(`${this.apiBaseUrl}/api/Portfolio/${encodeURIComponent(portfolioId)}`).pipe(
      catchError(err => {
        this.notification.showErrorObjectMessage(err);
        return throwError(() => err);
      })
    );
  }

  // Create a new portfolio
  createPortfolio(request: CreatePortfolioRequest): Observable<CreatePortfolioResponse> {
    return this.http.post<CreatePortfolioResponse>(`${this.apiBaseUrl}/api/Portfolio/CreatePortfolio`, request).pipe(
      catchError(err => {
        this.notification.showErrorObjectMessage(err);
        return throwError(() => err);
      })
    );
  }

  // GET /api/portfolio/portfoliosByGroup?portfolioGroupId=<id>
  getPortfoliosByGroup(portfolioGroupId: number): Observable<CommonPortfolio[]> {
    const params = new HttpParams().set('portfolioGroupId', portfolioGroupId.toString());
    return this.http.get<CommonPortfolio[]>(`${this.apiBaseUrl}/api/portfolio/portfoliosByGroup`, { params }).pipe(
      catchError(err => {
        this.notification.showErrorObjectMessage(err);
        return throwError(() => err);
      })
    );
  }
}
